import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, Keyboard, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrandMark, VerifyResult, PRIVACY_NOTE } from "@/pages/PublicVerify";

const tokenFrom = (text) => text.trim().split("?")[0].split("#")[0].replace(/\/+$/, "").split("/").pop();

function QrTab() {
  const navigate = useNavigate();
  const scanner = useRef(null);
  const [cam, setCam] = useState("off");
  useEffect(() => () => { try { scanner.current?.stop(); } catch { /* già fermo */ } }, []);
  const start = async () => {
    setCam("starting");
    await new Promise((r) => requestAnimationFrame(r));
    try {
      scanner.current = scanner.current || new Html5Qrcode("public-qr-reader", { verbose: false });
      await scanner.current.start({ facingMode: "environment" },
        { fps: 10, qrbox: (w, h) => { const s = Math.max(50, Math.floor(Math.min(w, h) * 0.72)); return { width: s, height: s }; } },
        async (text) => {
          try { await scanner.current.stop(); } catch { /* già fermo */ }
          navigate(`/v/${encodeURIComponent(tokenFrom(text) || "non-valido")}`);
        }, () => {});
      setCam("on");
    } catch {
      try { scanner.current?.clear(); } catch { /* niente da pulire */ }
      scanner.current = null;
      setCam("error");
    }
  };
  return (
    <div>
      <div id="public-qr-reader" className={`overflow-hidden rounded-xl bg-slate-900 ${cam === "off" || cam === "error" ? "hidden" : "min-h-[280px]"}`} data-testid="public-qr-reader" />
      {cam !== "on" && (
        <Button onClick={start} disabled={cam === "starting"} className="h-12 w-full bg-[#0F2042] text-base hover:bg-[#1E3A8A]" data-testid="public-qr-start-btn">
          {cam === "starting" ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Camera className="mr-2 h-5 w-5" />}Avvia fotocamera
        </Button>
      )}
      {cam === "error" && <p className="mt-3 text-sm text-red-600" data-testid="public-qr-error">Fotocamera non disponibile. Consenti l'accesso alla fotocamera o usa «Inserisci codice».</p>}
      <p className="mt-3 text-xs text-slate-500">Inquadra il QR code stampato sulla licenza.</p>
    </div>
  );
}

function CodeTab() {
  const [num, setNum] = useState("");
  const [surname, setSurname] = useState("");
  const [state, setState] = useState(undefined);
  const submit = async (e) => {
    e.preventDefault();
    setState(null);
    try {
      const { data } = await api.post("/public/verify-code", { license_number: num, last_name: surname });
      setState(data);
    } catch (err) {
      setState(err.response?.status === 429 ? { error: true, message: "Troppe verifiche ravvicinate: riprova tra un minuto." } : { error: true });
    }
  };
  return (
    <div>
      <form onSubmit={submit} className="space-y-3 text-left" data-testid="public-code-form">
        <div>
          <label htmlFor="lic-num" className="mb-1.5 block text-sm font-medium text-slate-700">Codice licenza</label>
          <Input id="lic-num" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Es. MSP-KART-2026-00001" className="font-mono uppercase" required data-testid="public-code-number-input" />
        </div>
        <div>
          <label htmlFor="lic-surname" className="mb-1.5 block text-sm font-medium text-slate-700">Cognome dell'atleta</label>
          <Input id="lic-surname" value={surname} onChange={(e) => setSurname(e.target.value)} autoComplete="off" required data-testid="public-code-surname-input" />
        </div>
        <Button type="submit" disabled={state === null} className="h-11 w-full bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="public-code-submit-btn">Verifica</Button>
      </form>
      {state !== undefined && <div className="mt-6 border-t border-slate-200 pt-6" data-testid="public-code-result"><VerifyResult state={state} /></div>}
    </div>
  );
}

export default function PublicCheck() {
  const [tab, setTab] = useState("qr");
  const tabs = [["qr", Camera, "Scansiona QR"], ["code", Keyboard, "Inserisci codice"]];
  return (
    <div className="flex min-h-screen items-start justify-center bg-[#F8FAFC] px-5 py-10 sm:items-center">
      <div className="card-surface w-full max-w-md p-6 fade-up sm:p-8" data-testid="public-check-card">
        <BrandMark />
        <h1 className="text-center font-display text-2xl font-black tracking-tight text-slate-900">Verifica licenza</h1>
        <div className="my-6 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
          {tabs.map(([k, I, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`public-check-tab-${k}`}
              className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-[#0F2042] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              <I className="h-4 w-4" />{label}
            </button>
          ))}
        </div>
        {tab === "qr" ? <QrTab /> : <CodeTab />}
        <p className="mt-6 text-center text-xs leading-relaxed text-slate-500">{PRIVACY_NOTE}</p>
        <Link to="/" className="mt-4 flex items-center justify-center gap-1.5 text-sm font-medium text-blue-800 hover:underline" data-testid="public-check-home-link"><ArrowLeft className="h-4 w-4" />Calendario gare</Link>
      </div>
    </div>
  );
}
